import { ArrowGo, EmoCool, EmoPass, EmoUserHim } from "@assets"
import { useForm, usePage } from "@inertiajs/react"
import type React from "react"
import { useState } from "react"
import { FcGoogle } from "react-icons/fc"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { signInPath, signUpPath } from "@/routes"

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  defaultTab?: "login" | "register"
}

export function AuthModal({
  isOpen,
  onClose,
  defaultTab = "login",
}: AuthModalProps) {
  const [activeTab, setActiveTab] = useState(defaultTab)
  const [registerStep, setRegisterStep] = useState<1 | 2>(1)

  const loginForm = useForm({
    email: "",
    password: "",
  })

  const registerForm = useForm({
    name: "",
    email: "",
    password: "",
    password_confirmation: "",
  })

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    loginForm.post(signInPath(), {
      onSuccess: () => {
        onClose()
      },
      onError: () => {
        toast.error("Credenciales inválidas")
      },
    })
  }

  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault()
    registerForm.post(signUpPath(), {
      onSuccess: () => {
        // Backend now handles showing welcome modal via props
        toast.success("¡Cuenta creada exitosamente!")
        onClose()
      },
      onError: () => {
        toast.error("Error al crear la cuenta")
      },
    })
  }

  const { props } = usePage()
  // eslint-disable-next-line react/prop-types
  const isMobileApp = (props.app as { mobile_app?: boolean })?.mobile_app

  const handleGoogleLogin = async () => {
    if (isMobileApp) {
      const { handleNativeGoogleLogin } = await import("@/lib/native-auth")
      await handleNativeGoogleLogin()
    } else {
      window.location.assign("/auth/google")
    }
  }

  const isLogin = activeTab === "login"
  const isRegisterStep1 = activeTab === "register" && registerStep === 1

  const titleContent = isLogin ? (
    <>
      <span className="text-chart-5 h-[25px] text-[18px] leading-[27px] font-semibold tracking-[0px]">
        Acceder a CurrAr
      </span>
      <img src={EmoCool} alt="" className="size-12" />
    </>
  ) : isRegisterStep1 ? (
    <>
      <span className="text-chart-5 h-[25px] text-[18px] leading-[27px] font-semibold tracking-[0px]">
        Nuevo usuario
      </span>
      <img src={EmoUserHim} alt="" className="size-11" />
    </>
  ) : (
    <div className="flex items-end">
      <span className="text-chart-5 h-[25px] text-[18px] leading-[27px] font-semibold tracking-[0px]">
        Nueva Contraseña
      </span>
      <img src={EmoPass} alt="" className="-mb-1 size-11" />
    </div>
  )

  const descriptionText = isLogin
    ? "Accede para participar en las comunidades argentinas de España"
    : isRegisterStep1
      ? "Recomendamos ingresar con Google o podés crear una nueva cuenta"
      : "Crea tu contraseña para acceder de forma segura a la comunidad"

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="px-0 sm:max-w-[480px]">
        {activeTab === "register" && registerStep === 2 && (
          <button
            type="button"
            onClick={() => setRegisterStep(1)}
            className="ring-offset-background focus:ring-ring text-primary absolute top-4 left-4 rounded-xs transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none"
          >
            <div className="text-primary flex items-center gap-0.5 text-[13px] tracking-[0.13px]">
              <ArrowGo className="fill-primary size-4 rotate-180" />
              Volver
            </div>
          </button>
        )}
        <DialogHeader className="px-6">
          <DialogTitle className="flex items-center justify-center gap-0.5 text-center text-balance">
            {titleContent}
          </DialogTitle>
          <DialogDescription className="mt-[14px] text-center text-[13px] tracking-[0.2px]">
            {descriptionText}
          </DialogDescription>
        </DialogHeader>

        <div className="w-full px-[30px]">
          {registerStep !== 2 ? (
            <>
              <Button
                type="button"
                variant="default"
                className="my-[17px] h-[49px] w-full rounded-[14px] text-[15px] leading-[23px] font-medium tracking-[0.45px] text-white"
                onClick={() => {
                  void handleGoogleLogin()
                }}
              >
                <div className="bg-card mr-2 flex h-[32px] w-[32px] items-center justify-center rounded-full">
                  <FcGoogle className="size-7" />
                </div>
                {activeTab === "login"
                  ? "Ingresar con Google"
                  : "Registrarse con Google"}
              </Button>
              <div className="border-chart-4 mt-[5px] mb-[17px] w-full border-t" />
            </>
          ) : (
            <div className="mt-[5px] mb-[17px] w-full" />
          )}

          {/* Login Form */}
          {activeTab === "login" && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-[5px]">
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="@"
                  value={loginForm.data.email}
                  onChange={(e) => loginForm.setData("email", e.target.value)}
                  className="placeholder-auth"
                  required
                />
                {loginForm.errors.email && (
                  <p className="text-destructive text-sm">
                    {loginForm.errors.email}
                  </p>
                )}
              </div>

              <div className="space-y-[5px]">
                <Label htmlFor="login-password">Contraseña</Label>
                <Input
                  id="login-password"
                  type="password"
                  placeholder="****"
                  value={loginForm.data.password}
                  onChange={(e) =>
                    loginForm.setData("password", e.target.value)
                  }
                  className="placeholder-auth"
                  required
                />
                {loginForm.errors.password && (
                  <p className="text-destructive text-sm">
                    {loginForm.errors.password}
                  </p>
                )}
              </div>

              <Button
                className="text-chart-5 bg-card mt-[6px] mb-[11px] h-[49px] w-full rounded-[14px] px-[24px] py-[14px] text-[15px] leading-[23px] font-medium tracking-[0.45px] shadow-[0_0_8px_#00000029]"
                disabled={loginForm.processing}
              >
                {loginForm.processing ? "Iniciando sesión..." : "Ingresar"}
              </Button>
            </form>
          )}

          {/* Register Form - two-step flow */}
          {activeTab === "register" && registerStep === 1 && (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                setRegisterStep(2)
              }}
              className="space-y-4"
            >
              <div className="space-y-[5px]">
                <Label htmlFor="register-name">Nombre completo</Label>
                <Input
                  id="register-name"
                  type="text"
                  placeholder="Tu nombre completo"
                  value={registerForm.data.name}
                  onChange={(e) => registerForm.setData("name", e.target.value)}
                  className="placeholder-auth"
                  required
                />
                {registerForm.errors.name && (
                  <p className="text-destructive text-sm">
                    {registerForm.errors.name}
                  </p>
                )}
              </div>

              <div className="space-y-[5px]">
                <Label htmlFor="register-email">Email</Label>
                <Input
                  id="register-email"
                  type="email"
                  placeholder="@"
                  value={registerForm.data.email}
                  onChange={(e) =>
                    registerForm.setData("email", e.target.value)
                  }
                  className="placeholder-auth"
                  required
                />
                {registerForm.errors.email && (
                  <p className="text-destructive text-sm">
                    {registerForm.errors.email}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="text-chart-5 bg-card mt-[6px] mb-[11px] h-[49px] w-full rounded-[14px] px-[24px] py-[14px] text-[15px] leading-[23px] font-medium tracking-[0.45px] shadow-[0_0_8px_#00000029]"
              >
                Continuar
              </Button>
            </form>
          )}

          {activeTab === "register" && registerStep === 2 && (
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="space-y-[5px]">
                <Label htmlFor="register-password">Contraseña</Label>
                <Input
                  id="register-password"
                  type="password"
                  placeholder="Mínimo 6 caracteres"
                  value={registerForm.data.password}
                  onChange={(e) =>
                    registerForm.setData("password", e.target.value)
                  }
                  className="placeholder-auth"
                  required
                  minLength={6}
                />
                {registerForm.errors.password && (
                  <p className="text-destructive text-sm">
                    {registerForm.errors.password}
                  </p>
                )}
              </div>

              <div className="space-y-[5px]">
                <Label htmlFor="register-password-confirmation">
                  Confirmar contraseña
                </Label>
                <Input
                  id="register-password-confirmation"
                  type="password"
                  placeholder="Confirma tu contraseña"
                  value={registerForm.data.password_confirmation}
                  onChange={(e) =>
                    registerForm.setData(
                      "password_confirmation",
                      e.target.value,
                    )
                  }
                  className="placeholder-auth"
                  required
                  minLength={6}
                />
                {registerForm.errors.password_confirmation && (
                  <p className="text-destructive text-sm">
                    {registerForm.errors.password_confirmation}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="mt-[6px] mb-[11px] h-[49px] w-full rounded-[14px] px-[24px] py-[14px] text-[15px] leading-[23px] font-medium tracking-[0.45px]"
                disabled={registerForm.processing}
              >
                {registerForm.processing ? "Creando cuenta..." : "Crear Cuenta"}
              </Button>
            </form>
          )}

          <div className="text-chart-5 text-center text-[13px] leading-[20px] font-normal tracking-[0.2px]">
            {activeTab === "login" ? (
              <>
                ¿No tienes cuenta?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab("register")
                    setRegisterStep(1)
                  }}
                  className="text-chart-3 text-center text-[12px] leading-[18px] font-medium tracking-[0.18px]"
                >
                  Regístrate aquí
                </button>
              </>
            ) : (
              <>
                ¿Ya tienes cuenta?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab("login")
                    setRegisterStep(1)
                  }}
                  className="text-chart-3 text-center text-[12px] leading-[18px] font-medium tracking-[0.18px]"
                >
                  Inicia sesión
                </button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
